
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    IconButton,
    Paper,
    CircularProgress,
    Card,
    useTheme,
    alpha,
    Avatar,
} from '@mui/material';
import {
    Add as AddIcon,
    DeleteOutline as DeleteOutlineIcon,
    Image as ImageIcon,
    Close as CloseIcon,
    Collections as CollectionsIcon,
    CalendarMonth as CalendarMonthIcon,
    FolderShared as FolderSharedIcon,
    CloudUpload as CloudUploadIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { galleryService } from '../service/galleryService';
import { getImageUrl } from '../service/api';
import dayjs from 'dayjs';
import SuccessNotification from '../components/SuccessNotification';

export default function Gallery() {
    const theme = useTheme();
    const [galleries, setGalleries] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Delete State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [galleryToDelete, setGalleryToDelete] = useState(null);

    // Upload State
    const [uploadFormData, setUploadFormData] = useState({
        title: '',
        images: []
    });
    const [uploadPreviews, setUploadPreviews] = useState([]);

    // Viewer State
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [viewerIndex, setViewerIndex] = useState(0);

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchGalleries();
    }, []);

    const fetchGalleries = async () => {
        try {
            const response = await galleryService.getGalleries();
            if (response.success && response.data) {
                setGalleries(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching galleries", error);
        }
    };

    // --- Create Gallery Handlers ---
    const handleOpenUpload = () => {
        setUploadFormData({ title: '', images: [] });
        setUploadPreviews([]);
        setOpen(true);
    };

    const handleCloseUpload = () => {
        setOpen(false);
    };

    const handleUploadFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Limit to 30 as per RN code requirement
        const currentCount = uploadFormData.images.length;
        const remainingSpace = 30 - currentCount;
        const filesToAdd = files.slice(0, remainingSpace);

        setUploadFormData(prev => ({
            ...prev,
            images: [...prev.images, ...filesToAdd]
        }));

        const newPreviews = filesToAdd.map(file => URL.createObjectURL(file));
        setUploadPreviews(prev => [...prev, ...newPreviews]);
    };

    const handleRemoveUploadImage = (index) => {
        setUploadFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
        setUploadPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleCreateGallery = async () => {
        if (uploadFormData.images.length === 0) {
            alert('Please select at least one image');
            return;
        }

        setLoading(true);
        try {
            // Adapted for Web: Service likely needs update or we handle here. 
            // Assuming service handles title + array of Files or we constructed FormData correctly in service.
            // Previous steps ensured galleryService.createGallery creates FormData correctly.
            const response = await galleryService.createGallery(
                uploadFormData.title || 'Untitled Gallery',
                uploadFormData.images
            );

            if (response.success) {
                fetchGalleries();
                handleCloseUpload();
                setNotification({ open: true, message: 'Gallery created successfully!', severity: 'success' });
            } else {
                alert(response.message || "Operation failed");
            }
        } catch (error) {
            console.error("Error creating gallery", error);
            alert("Error creating gallery");
        } finally {
            setLoading(false);
        }
    };

    // --- Delete Handlers ---
    const confirmDeleteGallery = (id) => {
        setGalleryToDelete(id);
        setDeleteModalOpen(true);
    };

    const handleDeleteGallery = async () => {
        if (!galleryToDelete) return;
        setLoading(true);
        try {
            await galleryService.deleteGallery(galleryToDelete);
            fetchGalleries();
            setDeleteModalOpen(false);
            setGalleryToDelete(null);
            setNotification({ open: true, message: 'Gallery deleted successfully!', severity: 'success' });
        } catch (error) {
            console.error("Error deleting gallery", error);
        } finally {
            setLoading(false);
        }
    };

    // --- Viewer Handlers ---
    const openViewer = (images, index = 0) => {
        if (!images || images.length === 0) return;
        setViewerImages(images);
        setViewerIndex(index);
        setViewerOpen(true);
    };

    const handleNextImage = (e) => {
        e.stopPropagation();
        setViewerIndex(prev => (prev + 1) % viewerImages.length);
    };

    const handlePrevImage = (e) => {
        e.stopPropagation();
        setViewerIndex(prev => (prev - 1 + viewerImages.length) % viewerImages.length);
    };

    const [searchTerm, setSearchTerm] = useState('');

    const filteredGalleries = galleries.filter(g =>
        (g.title || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box sx={{ width: '100%', pb: 5 }}>
            {/* Header Area */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    mb: { xs: 3, sm: 4 },
                    p: { xs: 2, sm: 3 },
                    borderRadius: 4,
                    bgcolor: 'white',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                    gap: 2
                }}
            >
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b', letterSpacing: -0.5, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                        Gallery
                    </Typography>
                </Box>

                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenUpload}
                        fullWidth
                        sx={{
                            borderRadius: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3,
                            py: 1.2,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: `0 8px 20px -6px ${alpha(theme.palette.primary.main, 0.5)}`,
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 12px 25px -6px ${alpha(theme.palette.primary.main, 0.6)}`,
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        Create Album
                    </Button>
                </Box>
            </Box>

            {filteredGalleries.length === 0 ? (
                <Box sx={{
                    textAlign: 'center',
                    py: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}>
                    <Box sx={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3
                    }}>
                        <CollectionsIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.2) }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#475569', mb: 1 }}>
                        {searchTerm ? 'No matching albums found' : 'Your Gallery is Empty'}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, maxWidth: 400 }}>
                        {searchTerm ? 'Try adjusting your search terms' : 'Start by creating your first photo album to share your events with the world.'}
                    </Typography>
                    {!searchTerm && (
                        <Button
                            variant="outlined"
                            onClick={handleOpenUpload}
                            sx={{ borderRadius: 3, px: 4, fontWeight: 700 }}
                        >
                            Get Started
                        </Button>
                    )}
                </Box>
            ) : (
                <Grid container spacing={4}>
                    {filteredGalleries.map((item) => {
                        const coverImage = item.images && item.images.length > 0 ? getImageUrl(item.images[0]) : null;
                        const totalImages = item.images ? item.images.length : 0;
                        const createdDate = item.createdAt ? dayjs(item.createdAt).format('MMMM D, YYYY') : 'Recently added';

                        return (
                            <Grid item xs={12} sm={6} md={4} key={item.id}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        borderRadius: 6,
                                        overflow: 'hidden',
                                        bgcolor: 'white',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                                        border: '1px solid',
                                        borderColor: 'rgba(0,0,0,0.05)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                                            '& .gallery-image': {
                                                transform: 'scale(1.1)'
                                            },
                                            '& .gallery-actions': {
                                                opacity: 1,
                                                transform: 'translateY(0)'
                                            }
                                        }
                                    }}
                                >
                                    {/* Image Container */}
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            height: 280,
                                            cursor: 'pointer',
                                            overflow: 'hidden',
                                            bgcolor: '#f8fafc'
                                        }}
                                        onClick={() => openViewer(item.images)}
                                    >
                                        {coverImage ? (
                                            <Box
                                                className="gallery-image"
                                                component="img"
                                                src={coverImage}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    transition: 'transform 0.5s'
                                                }}
                                            />
                                        ) : (
                                            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <ImageIcon sx={{ color: alpha(theme.palette.primary.main, 0.1), fontSize: 80 }} />
                                            </Box>
                                        )}

                                        {/* Overlay Badge */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 20,
                                                left: 20,
                                                bgcolor: 'rgba(255,255,255,0.9)',
                                                backdropFilter: 'blur(8px)',
                                                borderRadius: 2.5,
                                                px: 1.5,
                                                py: 0.8,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                            }}
                                        >
                                            <CollectionsIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                                            <Typography sx={{ color: 'primary.main', fontSize: 13, fontWeight: 800 }}>
                                                {totalImages} Photos
                                            </Typography>
                                        </Box>

                                        {/* Bottom Caption Overlay */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                right: 0,
                                                p: 3,
                                                background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: 'white',
                                                    fontWeight: 800,
                                                    fontSize: 20,
                                                    mb: 0.5
                                                }}
                                                noWrap
                                            >
                                                {item.title || 'Untitled Album'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <CalendarMonthIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }} />
                                                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600 }}>
                                                    {createdDate}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Quick Delete Action (Hidden usually) */}
                                        <Box
                                            className="gallery-actions"
                                            sx={{
                                                position: 'absolute',
                                                top: 20,
                                                right: 20,
                                                opacity: 0,
                                                transform: 'translateY(-10px)',
                                                transition: 'all 0.3s'
                                            }}
                                        >
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    confirmDeleteGallery(item.id);
                                                }}
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.9)',
                                                    backdropFilter: 'blur(8px)',
                                                    color: 'error.main',
                                                    '&:hover': { bgcolor: 'white', color: 'error.dark' }
                                                }}
                                            >
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    {/* Sub-info section */}
                                    <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid rgba(0,0,0,0.03)' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Avatar sx={{ width: 24, height: 24, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                                                <FolderSharedIcon sx={{ fontSize: 14 }} />
                                            </Avatar>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                                                Official Collection
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: alpha(theme.palette.primary.main, 0.6) }}>
                                            #{item.id?.toString().substring(0, 6)}
                                        </Typography>
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Upload Modal */}
            <Dialog
                open={open}
                onClose={handleCloseUpload}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 5,
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.2)'
                    }
                }}
            >
                <DialogTitle sx={{
                    p: 4,
                    pb: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b' }}>
                            New Gallery
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Organize your moments into a beautiful album
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseUpload} sx={{ bgcolor: '#f1f5f9' }} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 4, pt: 2 }}>
                    <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                                Album Title
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="e.g. Community Meetup 2024"
                                value={uploadFormData.title}
                                onChange={(e) => setUploadFormData(prev => ({ ...prev, title: e.target.value }))}
                                variant="outlined"
                                InputProps={{
                                    sx: { borderRadius: 3, bgcolor: '#f8fafc' }
                                }}
                            />
                        </Box>

                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', mb: 1, display: 'block' }}>
                                Upload Assets (Max 30)
                            </Typography>
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                sx={{
                                    borderStyle: 'dashed',
                                    borderWidth: 2,
                                    borderRadius: 4,
                                    minHeight: 120,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    color: 'primary.main',
                                    borderColor: alpha(theme.palette.primary.main, 0.2),
                                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                                    textTransform: 'none',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                                        transform: 'scale(1.01)'
                                    }
                                }}
                            >
                                <Box sx={{
                                    p: 1.5,
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    borderRadius: 2,
                                    mb: 0.5
                                }}>
                                    <CloudUploadIcon sx={{ fontSize: 28 }} />
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                        {uploadPreviews.length > 0 ? `${uploadPreviews.length} images selected` : 'Browse images from your device'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                                        Supports JPG, PNG (Max 10MB each)
                                    </Typography>
                                </Box>
                                <input
                                    type="file"
                                    hidden
                                    multiple
                                    accept="image/*"
                                    onChange={handleUploadFileChange}
                                />
                            </Button>
                        </Box>

                        {/* Previews Grid */}
                        {uploadPreviews.length > 0 && (
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                                gap: 2,
                                maxHeight: 200,
                                overflowY: 'auto',
                                p: 1,
                                bgcolor: '#f8fafc',
                                borderRadius: 3
                            }}>
                                {uploadPreviews.map((src, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            position: 'relative',
                                            aspectRatio: '1/1',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        <Box component="img" src={src} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <IconButton
                                            size="small"
                                            onClick={() => handleRemoveUploadImage(index)}
                                            sx={{
                                                position: 'absolute',
                                                top: 4,
                                                right: 4,
                                                bgcolor: 'rgba(0,0,0,0.6)',
                                                color: 'white',
                                                p: 0.5,
                                                '&:hover': { bgcolor: 'error.main' }
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 12 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 4, pt: 1 }}>
                    <Button
                        onClick={handleCreateGallery}
                        variant="contained"
                        fullWidth
                        disabled={loading}
                        sx={{
                            py: 1.8,
                            borderRadius: 3,
                            fontWeight: 800,
                            fontSize: '1rem',
                            textTransform: 'none',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: `0 8px 20px -6px ${alpha(theme.palette.primary.main, 0.4)}`,
                        }}
                    >
                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Publish Album'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}
            >
                <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
                    <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'error.lighter', color: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>Delete Gallery?</Typography>
                    <Typography variant="body2" color="text.secondary">
                        This will permanently remove the gallery and all its images. Are you sure?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 4, justifyContent: 'center', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setDeleteModalOpen(false)}
                        sx={{ borderRadius: 2, px: 3, flex: 1, borderColor: 'divider', color: 'text.primary' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteGallery}
                        disabled={loading}
                        sx={{ borderRadius: 2, px: 3, flex: 1, boxShadow: 'none' }}
                    >
                        {loading ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Viewer Fullscreen Modal */}
            <Dialog
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                fullScreen
                PaperProps={{ sx: { bgcolor: 'black' } }}
            >
                <Box sx={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {/* Header Controls */}
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, p: 2, display: 'flex', justifyContent: 'flex-end', zIndex: 10 }}>
                        <IconButton onClick={() => setViewerOpen(false)} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Navigation Buttons */}
                    {viewerImages.length > 1 && (
                        <>
                            <IconButton
                                onClick={handlePrevImage}
                                sx={{ position: 'absolute', left: 20, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, zIndex: 10 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <IconButton
                                onClick={handleNextImage}
                                sx={{ position: 'absolute', right: 20, color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }, zIndex: 10 }}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </>
                    )}

                    {/* Image */}
                    {viewerImages.length > 0 && (
                        <Box
                            component="img"
                            src={getImageUrl(viewerImages[viewerIndex])}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    )}

                    {/* Footer Info */}
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, p: 3, background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)', textAlign: 'center' }}>
                        <Typography sx={{ color: 'white', fontWeight: 600, letterSpacing: 1 }}>
                            {viewerIndex + 1} / {viewerImages.length}
                        </Typography>
                    </Box>
                </Box>
            </Dialog>

            <SuccessNotification
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={() => setNotification({ ...notification, open: false })}
            />
        </Box>
    );
}
